// User Profile Management
// Firestore is the single source of truth for all profile data.
// Profile path: users/{userId}/profile/data

import { doc, getDoc, setDoc } from 'firebase/firestore';

const PROFILE_PATH = (userId) => `users/${userId}/profile/data`;

const DEFAULT_PROFILE = {
  profilePicture: null,
  venmoUsername: '',
  displayName: '',
  username: '',
  createdAt: new Date().toISOString()
};

export const UserProfile = {
  // Get user profile from Firestore
  getProfile: async (userId, db) => {
    if (!userId || !db) {
      console.warn('UserProfile.getProfile: Missing userId or db');
      return { ...DEFAULT_PROFILE };
    }

    try {
      const profileDoc = await getDoc(doc(db, PROFILE_PATH(userId)));
      if (profileDoc.exists()) {
        const data = profileDoc.data();
        return { ...DEFAULT_PROFILE, ...data };
      }
      return { ...DEFAULT_PROFILE };
    } catch (error) {
      console.error('Error getting profile from Firestore:', error);
      return { ...DEFAULT_PROFILE };
    }
  },

  // Save profile data to Firestore (merge with existing)
  saveProfile: async (userId, data, db) => {
    if (!userId || !data || !db) {
      console.error('UserProfile.saveProfile: Missing required params');
      return false;
    }

    try {
      await setDoc(doc(db, PROFILE_PATH(userId)), {
        ...data,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      return true;
    } catch (error) {
      console.error('Error saving profile to Firestore:', error);
      return false;
    }
  },

  // Compress image to fit Firestore field size limit (~800KB)
  compressImage: (file, maxSizeKB = 800) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const MAX_SIZE = 400;
        let { width, height } = img;

        if (width > height) {
          if (width > MAX_SIZE) {
            height = (height * MAX_SIZE) / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width = (width * MAX_SIZE) / height;
            height = MAX_SIZE;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.8;
        let base64Data = canvas.toDataURL('image/jpeg', quality);

        while (base64Data.length > maxSizeKB * 1024 && quality > 0.1) {
          quality -= 0.1;
          base64Data = canvas.toDataURL('image/jpeg', quality);
        }

        console.log(`Image compressed: ${Math.round(base64Data.length / 1024)}KB at ${Math.round(quality * 100)}% quality`);
        resolve(base64Data);
      };

      img.onerror = () => reject(new Error('Failed to load image for compression'));
      img.src = URL.createObjectURL(file);
    });
  },

  // Upload profile picture: compress and save to Firestore
  uploadProfilePicture: async (userId, file, db) => {
    if (!file) throw new Error('No file provided');
    if (file.size > 5 * 1024 * 1024) throw new Error('File size must be less than 5MB');
    if (!file.type.startsWith('image/')) throw new Error('File must be an image');

    const compressedBase64 = await UserProfile.compressImage(file);
    const success = await UserProfile.saveProfile(userId, { profilePicture: compressedBase64 }, db);
    if (success) {
      return compressedBase64;
    }
    throw new Error('Failed to save profile picture');
  },

  // Remove profile picture
  removeProfilePicture: async (userId, db) => {
    return UserProfile.saveProfile(userId, { profilePicture: null }, db);
  },

  // One-time migration: copy localStorage data to Firestore, then clear localStorage
  migrateFromLocalStorage: async (userId, db) => {
    const LEGACY_KEY = 'splitapp_user_profile';
    try {
      const stored = localStorage.getItem(LEGACY_KEY);
      if (!stored) return false;

      const profiles = JSON.parse(stored);
      const localProfile = profiles[userId];
      if (!localProfile) return false;

      // Only migrate if there's meaningful data
      if (!localProfile.displayName && !localProfile.venmoUsername && !localProfile.username && !localProfile.profilePicture) {
        return false;
      }

      // Check if Firestore already has data (don't overwrite)
      const existing = await getDoc(doc(db, PROFILE_PATH(userId)));
      if (existing.exists()) {
        const existingData = existing.data();
        // If Firestore already has meaningful data, skip migration
        if (existingData.displayName || existingData.venmoUsername || existingData.username || existingData.profilePicture) {
          console.log('Firestore profile already exists, skipping localStorage migration');
          // Clean up localStorage
          delete profiles[userId];
          if (Object.keys(profiles).length === 0) {
            localStorage.removeItem(LEGACY_KEY);
          } else {
            localStorage.setItem(LEGACY_KEY, JSON.stringify(profiles));
          }
          return false;
        }
      }

      // Migrate to Firestore
      await setDoc(doc(db, PROFILE_PATH(userId)), {
        ...localProfile,
        migratedFrom: 'localStorage',
        migratedAt: new Date().toISOString()
      }, { merge: true });
      console.log('Profile migrated from localStorage to Firestore for user:', userId);

      // Clean up localStorage
      delete profiles[userId];
      if (Object.keys(profiles).length === 0) {
        localStorage.removeItem(LEGACY_KEY);
      } else {
        localStorage.setItem(LEGACY_KEY, JSON.stringify(profiles));
      }

      return true;
    } catch (error) {
      console.error('Migration from localStorage failed:', error);
      return false;
    }
  }
};
